import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import Backend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
// don't want to use this?
// have a look at the Quick start guide
// for passing in lng and translations on init

// Supported languages
const supportedLanguages = ['en', 'de'];

// Validate and normalize language from localStorage
const validateLanguage = (lng: string | null): string => {
    if (!lng) return 'de';
    
    // Extract base language code (e.g., "en" from "en-US", "tr" from "tr-TR")
    const baseLang = lng.includes('-') ? lng.split('-')[0].toLowerCase() : lng.toLowerCase();
    
    // Check if the base language is supported
    if (supportedLanguages.includes(baseLang)) {
        return baseLang;
    }
    
    // If not supported, return default language
    return 'de';
};

// Check localStorage and validate language before initialization
// If no language is stored, set default to 'de-DE' (will be normalized to 'de' by load: "languageOnly")
const storedLanguage = localStorage.getItem('i18nextLng');
if (!storedLanguage) {
    // First time user - set default language to 'de-DE'
    localStorage.setItem('i18nextLng', 'de-DE');
} else {
    const validatedLang = validateLanguage(storedLanguage);
    const baseStoredLang = storedLanguage.includes('-') ? storedLanguage.split('-')[0].toLowerCase() : storedLanguage.toLowerCase();
    // Update localStorage with validated language if different
    if (validatedLang !== baseStoredLang) {
        // Store as 'de-DE' or 'en-US' format for consistency
        localStorage.setItem('i18nextLng', validatedLang === 'de' ? 'de-DE' : 'en-US');
    }
}

i18n
    // load translation using http -> see /public/locales (i.e. https://github.com/i18next/react-i18next/tree/master/example/react/public/locales)
    // learn more: https://github.com/i18next/i18next-http-backend
    // want your translations to be loaded from a professional CDN? => https://github.com/locize/react-tutorial#step-2---use-the-locize-cdn
    .use(Backend)
    // detect user language
    // learn more: https://github.com/i18next/i18next-browser-languageDetector
    .use(LanguageDetector)
    // pass the i18n instance to react-i18next.
    .use(initReactI18next)
    // init i18next
    // for all options read: https://www.i18next.com/overview/configuration-options
    .init({
        fallbackLng: 'de',
        supportedLngs: supportedLanguages,
        debug: false,
        load: "languageOnly",
        interpolation: {
            escapeValue: false, // not needed for react as it escapes by default
        },
        backend: {
            loadPath: "/locales/{{lng}}/translation.json",
            allowMultiLoading: true,
        },
        react: {
            useSuspense: false,
        },
        nonExplicitSupportedLngs: false,
        cleanCode: true,
        detection: {
            // Order of detection: localStorage first (which we've set to 'de-DE' if empty), then browser language
            // Since we set localStorage to 'de-DE' before init, it will be used as default
            order: ['localStorage', 'navigator'],
            // Only look in localStorage and navigator
            lookupLocalStorage: 'i18nextLng',
            lookupNavigator: 'language',
            // Cache detected language in localStorage
            caches: ['localStorage'],
        },
    } as any).then(() => {
    // Validate language after initialization completes
    const currentLang = i18n.language;
    if (currentLang) {
        const validatedLang = validateLanguage(currentLang);
        const baseLang = currentLang.includes('-') ? currentLang.split('-')[0].toLowerCase() : currentLang.toLowerCase();
        if (validatedLang !== baseLang) {
            i18n.changeLanguage(validatedLang);
        }
    }
});

// Add language change listener to validate language when it changes
i18n.on('languageChanged', (lng) => {
    const validatedLang = validateLanguage(lng);
    const baseLng = lng.includes('-') ? lng.split('-')[0].toLowerCase() : lng.toLowerCase();
    if (validatedLang !== baseLng) {
        // If language is not supported, change to default
        i18n.changeLanguage(validatedLang);
    }
});


export default i18n;
